<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { getFileIcon, getFileColor, formatFileSize, getFileExtension } from '@/utils/fileTypes'
import ProviderThumbnail from '@/components/Anonymization/ProviderThumbnail.vue'
import Provider from '@/api/Provider'

const props = defineProps({
  file: {
    type: File,
    required: true
  },
  attachment: {
    type: Object,
    default: null
  },
  modelValue: {
    type: Object,
    default: () => ({
      attribution: '',
      category: null,
      description: ''
    })
  },
  categories: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'remove'])

// Local metadata state
const metadata = ref({
  attribution: props.modelValue.attribution || '',
  category: props.modelValue.category || null,
  description: props.modelValue.description || ''
})

// Provider search state
const providers = ref([])
const providersLoading = ref(true)
const providerSearch = ref('')
const showProviderDropdown = ref(false)
const selectedProvider = ref(null)

// File info
const fileIcon = computed(() => getFileIcon(props.file))
const fileColor = computed(() => getFileColor(props.file))
const fileSize = computed(() => formatFileSize(props.file.size))
const fileExtension = computed(() => getFileExtension(props.file.name))

// Extraction count from attachment (backend returns extracted_count)
const extractionCount = computed(() => {
  if (!props.attachment) return 0
  return props.attachment.extracted_count || 0
})

// Filtered providers for dropdown
const filteredProviders = computed(() => {
  if (!providerSearch.value) {
    return providers.value
  }

  const search = providerSearch.value.toLowerCase()
  return providers.value.filter(provider => {
    const displayName = provider.display_name || provider.name
    return displayName.toLowerCase().includes(search) ||
           provider.name.toLowerCase().includes(search)
  })
})

// Character counter for description
const descriptionCharCount = computed(() => {
  return metadata.value.description ? metadata.value.description.length : 0
})

// Emit updates when metadata changes
watch(metadata, (newValue) => {
  emit('update:modelValue', newValue)
}, { deep: true })

// Provider search handlers
function handleProviderInput() {
  showProviderDropdown.value = true
  if (selectedProvider.value && providerSearch.value !== (selectedProvider.value.display_name || selectedProvider.value.name)) {
    selectedProvider.value = null
    metadata.value.attribution = ''
  }
}

function selectProvider(provider) {
  selectedProvider.value = provider
  metadata.value.attribution = provider.name
  providerSearch.value = provider.display_name || provider.name
  showProviderDropdown.value = false
}

function clearProvider() {
  selectedProvider.value = null
  metadata.value.attribution = ''
  providerSearch.value = ''
}

function handleProviderBlur() {
  setTimeout(() => {
    showProviderDropdown.value = false
  }, 200)
}

// Load providers on mount
async function loadProviders() {
  try {
    providers.value = await Provider.listProviders() || []
  } catch (err) {
    console.error('Failed to load providers:', err)
  } finally {
    providersLoading.value = false
  }
}

loadProviders()
</script>

<template>
  <div class="file-attachment-item">
    <!-- File Info Header -->
    <div class="file-header">
      <div class="file-icon-wrapper" :style="{ color: fileColor }">
        <font-awesome-icon :icon="fileIcon" class="file-icon" />
      </div>

      <div class="file-info">
        <div class="file-name" :title="file.name">{{ file.name }}</div>
        <div class="file-meta">
          <span class="file-type-badge">{{ fileExtension }}</span>
          <span class="file-size">{{ fileSize }}</span>
          <span v-if="extractionCount > 0" class="extraction-badge">
            <font-awesome-icon :icon="['fas', 'database']" />
            {{ extractionCount }} extracted
          </span>
        </div>
      </div>

      <button
        type="button"
        class="btn-remove-attachment"
        @click="emit('remove')"
        :disabled="disabled"
        title="Remove file"
      >
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
    </div>

    <!-- Metadata Fields -->
    <div class="file-metadata-grid">
      <!-- Attribution (Provider Search) -->
      <div class="metadata-field">
        <label class="metadata-label">Attribution</label>
        <div class="provider-select-container">
          <input
            v-model="providerSearch"
            type="text"
            class="metadata-input"
            placeholder="Search provider..."
            :disabled="disabled || providersLoading"
            @input="handleProviderInput"
            @focus="showProviderDropdown = true"
            @blur="handleProviderBlur"
            autocomplete="off"
          />
          <button
            v-if="selectedProvider"
            type="button"
            class="clear-provider-btn"
            @click="clearProvider"
            :disabled="disabled"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>

          <div v-if="showProviderDropdown && filteredProviders.length > 0" class="provider-dropdown">
            <div
              v-for="provider in filteredProviders"
              :key="provider.name"
              class="provider-option"
              @mousedown.prevent="selectProvider(provider)"
            >
              <ProviderThumbnail
                :data="{
                  name: provider.display_name || provider.name,
                  thumbnail_url: provider.thumbnail_url
                }"
              />
              <span class="provider-option-name">{{ provider.display_name || provider.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Category -->
      <div class="metadata-field">
        <label class="metadata-label">Category</label>
        <select
          v-model="metadata.category"
          class="metadata-select"
          :disabled="disabled"
        >
          <option :value="null">Not specified</option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">
            {{ cat.name }}
          </option>
        </select>
      </div>

      <!-- Description (Full Width) -->
      <div class="metadata-field metadata-field-full">
        <label class="metadata-label">Description</label>
        <textarea
          v-model="metadata.description"
          class="metadata-textarea"
          placeholder="Additional context about this file..."
          maxlength="500"
          rows="2"
          :disabled="disabled"
        ></textarea>
        <span class="char-counter">{{ descriptionCharCount }}/500</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.file-attachment-item {
  background: #1E252F;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.2s;
}

.file-attachment-item:hover {
  border-color: rgba(115, 87, 210, 0.3);
  background: rgba(30, 37, 47, 0.8);
}

/* File Header */
.file-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.file-icon-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  flex-shrink: 0;
}

.file-icon {
  font-size: 1.5rem;
}

.file-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.file-name {
  color: #E5E7EB;
  font-size: 0.9rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.file-type-badge {
  font-size: 0.65rem;
  font-weight: 700;
  color: #7357D2;
  text-transform: uppercase;
  padding: 0.2rem 0.5rem;
  background: rgba(115, 87, 210, 0.2);
  border-radius: 6px;
}

.file-size {
  font-size: 0.75rem;
  color: #9CA3AF;
}

.extraction-badge {
  font-size: 0.7rem;
  color: #10B981;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 500;
}

.extraction-badge svg {
  font-size: 0.65rem;
}

.btn-remove-attachment {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-remove-attachment:hover:not(:disabled) {
  background: rgba(252, 140, 136, 0.2);
  color: #fc8c88;
}

.btn-remove-attachment:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Metadata Fields */
.file-metadata-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.metadata-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  position: relative;
}

.metadata-field-full {
  grid-column: 1 / -1;
}

.metadata-label {
  font-size: 0.7rem;
  color: #9CA3AF;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metadata-input,
.metadata-select,
.metadata-textarea {
  padding: 0.5rem;
  background: #1F2631;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #E5E7EB;
  font-size: 0.85rem;
  transition: border-color 0.2s;
  font-family: inherit;
}

.metadata-input:focus,
.metadata-select:focus,
.metadata-textarea:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.5);
}

.metadata-input::placeholder,
.metadata-textarea::placeholder {
  color: #6B7280;
}

.metadata-textarea {
  resize: vertical;
  min-height: 60px;
  line-height: 1.4;
}

.char-counter {
  font-size: 0.7rem;
  color: #6B7280;
  text-align: right;
}

/* Provider Search */
.provider-select-container {
  position: relative;
}

.clear-provider-btn {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  font-size: 0.8rem;
}

.clear-provider-btn:hover:not(:disabled) {
  color: #E5E7EB;
  background: rgba(255, 255, 255, 0.05);
}

.clear-provider-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.provider-dropdown {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  background-color: #1B2029;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.provider-dropdown::-webkit-scrollbar {
  width: 8px;
}

.provider-dropdown::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.provider-dropdown::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.provider-dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.provider-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.provider-option:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.provider-option-name {
  color: #E5E7EB;
  font-size: 0.875rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .file-metadata-grid {
    grid-template-columns: 1fr;
  }

  .file-header {
    gap: 0.5rem;
  }

  .file-icon-wrapper {
    width: 36px;
    height: 36px;
  }

  .file-icon {
    font-size: 1.25rem;
  }
}
</style>
